import { useState } from 'react';
import { useOverlay } from './Overlay';
import { ChevronIcon } from './Icons';
import { addTask, dueOf, leafTasks, OUTCOMES, recordTask, removeTask, shortDay, TASK_GROUPS, type TaskGroup, type TedTask, useTedTasks, weekdays } from '@/lib/tedTasks';

// Ted's weekly sales list in a coaching group's Info, Tasks tab (Gary, 1 Nov 2026): grouped Leads, Customer
// follow-ups, Peace of Sleep and Other strategies, each group opening to that week's names. Tap the circle to
// record it: Done with an optional outcome and note, or Not done with a reason. Earlier weeks open from the chips.
// Tasks are batches (Gary, 2 Nov 2026): "Call 10 hot and warm leads" opens to its names, each ticked on its own; a batch
// closes itself once every name is recorded, or early (open names marked not reached).

function outcomeLine(t: TedTask): string | null {
  if (t.status === 'carried') return 'Moved to next week';
  if (t.status !== 'done' && t.status !== 'not_done') return null;
  const by = t.done_by_ted ? 'Ted' : t.done_by_name;
  return [t.status === 'done' ? 'Done' : 'Not done', t.reason, t.note, by ? `by ${by}` : null].filter(Boolean).join(' · ');
}

function OutcomeSheet({ task, openInside = 0, onDone }: { task: TedTask; openInside?: number; onDone: () => void }) {
  const { toast } = useOverlay();
  const recorded = task.status === 'done' || task.status === 'not_done';
  const [choice, setChoice] = useState<'done' | 'not_done' | null>(recorded ? task.status as 'done' | 'not_done' : null);
  const [reason, setReason] = useState<string | null>(task.reason);
  const [note, setNote] = useState(task.note ?? '');
  const [busy, setBusy] = useState(false);

  async function save(status: 'open' | 'done' | 'not_done') {
    setBusy(true);
    const err = await recordTask(task.id, status, status === 'open' ? null : reason, status === 'open' ? null : note.trim() || null);
    setBusy(false);
    if (err) { toast(err); return; }
    toast(status === 'open' ? 'Back on the list' : status === 'done' ? 'Done' : 'Saved');
    onDone();
  }

  return (
    <div className="chats-pad">
      <h2 className="chats-sheet-title">{task.title}</h2>
      {task.detail && <p className="chats-sheet-hint">{task.detail}</p>}
      {openInside > 0 && <p className="chats-sheet-hint">{`Closing it now marks the ${openInside} name${openInside === 1 ? '' : 's'} still open as not reached.`}</p>}
      <div className="chats-pair">
        <button type="button" className={choice === 'done' ? 'chats-btn' : 'chats-btn chats-btn--ghost'} aria-pressed={choice === 'done'}
          onClick={() => { setChoice('done'); setReason(null); }}>Done</button>
        <button type="button" className={choice === 'not_done' ? 'chats-btn chats-btn--danger' : 'chats-btn chats-btn--ghost'} aria-pressed={choice === 'not_done'}
          onClick={() => { setChoice('not_done'); setReason(null); }}>Not done</button>
      </div>
      {choice && (
        <>
          <p className="chats-field-label">{choice === 'done' ? 'Outcome (optional)' : 'Why not'}</p>
          <div className="chats-task-reasons">
            {OUTCOMES[choice].map((r) => (
              <button key={r} type="button" className={`chats-chip${reason === r ? ' active' : ''}`} aria-pressed={reason === r}
                onClick={() => setReason(reason === r ? null : r)}>{r}</button>
            ))}
          </div>
          <label className="chats-field-label" htmlFor="ted-task-note">Note (optional)</label>
          <textarea id="ted-task-note" className="chats-input chats-input--stack" rows={3} maxLength={500} value={note}
            onChange={(e) => setNote(e.target.value)} placeholder={choice === 'done' ? 'What happened' : 'What happened, and what next'} />
          <button type="button" className="chats-btn" disabled={busy || (choice === 'not_done' && !reason)} onClick={() => void save(choice)}>Save</button>
        </>
      )}
      {recorded && (
        <button type="button" className="chats-btn chats-btn--ghost chats-task-reopen" disabled={busy} onClick={() => void save('open')}>Put back on the list</button>
      )}
      {task.mine && (
        <button type="button" className="chats-btn chats-btn--ghost chats-task-reopen chats-set-bad" disabled={busy} onClick={() => {
          setBusy(true);
          void removeTask(task.id).then((err) => { setBusy(false); if (err) { toast(err); return; } toast('Removed'); onDone(); });
        }}>Remove from the list</button>
      )}
    </div>
  );
}

function AddSheet({ threadId, group, batch, onDone }: { threadId: string; group: TaskGroup; batch?: TedTask; onDone: () => void }) {
  const { toast } = useOverlay();
  const g = TASK_GROUPS.find((x) => x.key === group)!;
  const [title, setTitle] = useState('');
  const [due, setDue] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const clean = title.replace(/\s+/g, ' ').trim();
  return (
    <div className="chats-pad">
      <h2 className="chats-sheet-title">{batch ? `Add a name to ${batch.title}` : `Add to ${g.title}`}</h2>
      <label className="chats-field-label" htmlFor="ted-add-title">{batch ? 'Who or where' : 'The task'}</label>
      <input id="ted-add-title" className="chats-input" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus
        placeholder={batch ? (group === 'other' ? "e.g. Dr Smith's practice, Claremont" : group === 'pos' ? 'e.g. Paperbark Manor' : 'e.g. Jane Botha')
          : group === 'other' ? 'e.g. Drop vouchers at GP practices' : group === 'pos' ? 'e.g. Phone guest houses in Constantia' : 'e.g. Call Jane Botha'} />
      {!batch && <p className="chats-sheet-hint">Add the names under it once it is on the list.</p>}
      <p className="chats-field-label">Day (optional)</p>
      <div className="chats-task-reasons">
        {weekdays().map((d) => (
          <button key={d} type="button" className={`chats-chip${due === d ? ' active' : ''}`} aria-pressed={due === d} onClick={() => setDue(due === d ? null : d)}>{shortDay(d).split(' ')[0]}</button>
        ))}
      </div>
      <label className="chats-field-label" htmlFor="ted-add-note">Note (optional)</label>
      <textarea id="ted-add-note" className="chats-input chats-input--stack" rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why, or what to do" />
      <button type="button" className="chats-btn" disabled={busy || clean.length < 3} onClick={() => {
        setBusy(true);
        void addTask(threadId, group, clean, due, note.trim() || null, batch?.id ?? null).then((err) => { setBusy(false); if (err) { toast(err); return; } toast('Added'); onDone(); });
      }}>Add</button>
    </div>
  );
}

export function TedTasks({ threadId, bump = 0 }: { threadId: string; bump?: number }) {
  const { openSheet, closeSheet } = useOverlay();
  const [week, setWeek] = useState<string | null>(null);
  const { coaching, tasks, weeks } = useTedTasks(threadId, week, bump);
  // Groups with something open start open; a tap opens or closes one.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  if (!coaching) return null;

  const shown = week ?? weeks[0] ?? null;
  // Counts are names, not batches.
  const names = leafTasks(tasks);
  const done = names.filter((t) => t.status === 'done').length;
  const notDone = names.filter((t) => t.status === 'not_done').length;
  const open = names.filter((t) => t.status === 'open').length;
  const live = names.filter((t) => t.status !== 'carried').length;
  const inside = (b: TedTask) => tasks.filter((t) => t.parent_id === b.id);

  function row(t: TedTask, sub: boolean) {
    const out = outcomeLine(t);
    const state = t.status === 'done' ? ' chats-task-tick--done' : t.status === 'not_done' ? ' chats-task-tick--not' : '';
    const due = sub ? t.due_date : dueOf(t, tasks);
    return (
      <button key={t.id} type="button" className={`chats-set chats-task-item${sub ? ' chats-task-item--sub' : ''}`} disabled={t.status === 'carried'}
        onClick={() => openSheet(<OutcomeSheet task={t} onDone={closeSheet} />)}>
        <span className={`chats-task-tick${state}`} aria-hidden="true">{t.status === 'done' ? '✓' : t.status === 'not_done' ? '✕' : ''}</span>
        <div className="chats-set-main">
          <span className={t.status === 'done' ? 'chats-task-done' : undefined}>{t.title}{t.carried && !sub && <span className="chats-task-carried">Carried over</span>}</span>
          <small>{[t.detail, due ? `Due ${shortDay(due)}` : null, t.added_by_name ? `Added by ${t.mine ? 'you' : t.added_by_name}` : null].filter(Boolean).join(' · ')}</small>
          {out && <small className={`chats-task-outcome chats-task-outcome--${t.status === 'done' ? 'done' : 'not'}`}>{out}</small>}
        </div>
      </button>
    );
  }

  function batchRows(b: TedTask) {
    const items = inside(b);
    const recorded = items.filter((t) => t.status === 'done' || t.status === 'not_done').length;
    const openNames = items.filter((t) => t.status === 'open').length;
    const isOpen = toggled[b.id] ?? openNames > 0;
    return (
      <div key={b.id} className="chats-task-batch">
        <div className="chats-task-batch-head">
          <button type="button" className="chats-set chats-task-item" disabled={b.status === 'carried'}
            onClick={() => openSheet(<OutcomeSheet task={b} openInside={openNames} onDone={closeSheet} />)}>
            <span className={`chats-task-tick${b.status === 'done' ? ' chats-task-tick--done' : b.status === 'not_done' ? ' chats-task-tick--not' : ''}`} aria-hidden="true">{b.status === 'done' ? '✓' : b.status === 'not_done' ? '✕' : ''}</span>
            <div className="chats-set-main">
              <span className={b.status === 'done' ? 'chats-task-done' : undefined}><b>{b.title}</b>{b.carried && <span className="chats-task-carried">Carried over</span>}</span>
              <small>{[b.detail, b.due_date ? `By ${shortDay(b.due_date)}` : null, b.added_by_name ? `Added by ${b.mine ? 'you' : b.added_by_name}` : null].filter(Boolean).join(' · ')}</small>
              {outcomeLine(b) && <small className={`chats-task-outcome chats-task-outcome--${b.status === 'done' ? 'done' : 'not'}`}>{outcomeLine(b)}</small>}
            </div>
          </button>
          <button type="button" className="chats-task-batch-toggle" aria-expanded={isOpen} aria-label={isOpen ? 'Hide the names' : 'Show the names'}
            onClick={() => setToggled({ ...toggled, [b.id]: !isOpen })}>
            {items.length > 0 && <span className="chats-task-count">{`${recorded} of ${items.length}`}</span>}
            <span className={`chats-task-chev${isOpen ? ' chats-task-chev--open' : ''}`}><ChevronIcon /></span>
          </button>
        </div>
        {isOpen && items.map((t) => row(t, true))}
        {isOpen && week === null && b.status !== 'carried' && (
          <button type="button" className="chats-set chats-task-item chats-task-item--sub chats-task-add" onClick={() => openSheet(<AddSheet threadId={threadId} group={b.task_group} batch={b} onDone={closeSheet} />)}>
            <span className="chats-set-add" aria-hidden="true">+</span>
            <div className="chats-set-main"><span>Add a name</span></div>
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="chats-task-week">
        <b>{shown ? `Week of ${shortDay(shown)}` : 'This week'}</b>
        <span>{`${done} done · ${notDone} not done · ${open} open`}</span>
      </div>
      <div className="chats-task-progress" role="progressbar" aria-valuemin={0} aria-valuemax={live} aria-valuenow={done + notDone} aria-label="Recorded this week">
        <progress max={live || 1} value={done + notDone} />
      </div>
      {weeks.length > 1 && (
        <div className="chats-task-weeks" role="tablist" aria-label="Week">
          {weeks.slice(0, 6).map((w) => (
            <button key={w} type="button" role="tab" aria-selected={w === shown} className={`chats-chip${w === shown ? ' active' : ''}`} onClick={() => setWeek(w)}>
              {shortDay(w)}
            </button>
          ))}
        </div>
      )}
      {tasks.length === 0 && <p className="chats-sheet-hint chats-task-empty">No tasks this week yet. Ted adds them on Monday; open a group to add your own.</p>}
      {TASK_GROUPS.map((g) => {
        const top = tasks.filter((t) => t.task_group === g.key && !t.parent_id);
        const groupNames = names.filter((t) => t.task_group === g.key);
        // Earlier weeks: only groups that had something. This week: every group, so anything can be added.
        if (!top.length && week !== null) return null;
        const recorded = groupNames.filter((t) => t.status === 'done' || t.status === 'not_done').length;
        const isOpen = toggled[g.key] ?? groupNames.some((t) => t.status === 'open');
        return (
          <div key={g.key} className="chats-group">
            <button type="button" className="chats-set chats-task-head" aria-expanded={isOpen} onClick={() => setToggled({ ...toggled, [g.key]: !isOpen })}>
              <span className={`chats-task-icon chats-task-icon--${g.key}`} aria-hidden="true">{g.icon}</span>
              <div className="chats-set-main"><b>{g.title}</b><small>{g.goal}</small></div>
              <span className="chats-task-count">{`${recorded} of ${groupNames.length}`}</span>
              <span className={`chats-task-chev${isOpen ? ' chats-task-chev--open' : ''}`}><ChevronIcon /></span>
            </button>
            {/* Their own tasks can take names too, e.g. the practices for a voucher drop. */}
            {isOpen && top.map((t) => (inside(t).length || t.added_by_name ? batchRows(t) : row(t, false)))}
            {isOpen && week === null && (
              <button type="button" className="chats-set chats-task-item chats-task-add" onClick={() => openSheet(<AddSheet threadId={threadId} group={g.key} onDone={closeSheet} />)}>
                <span className="chats-set-add" aria-hidden="true">+</span>
                <div className="chats-set-main"><span>Add a task</span></div>
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}
